import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FixedSizeList, VariableSizeList } from 'react-window';
import clsx from 'clsx';
import type { LogLine } from '@/lib/logcat/parser';
import { levelClass } from '@/lib/logcat/parser';
import type { AnchorRect } from './TokenMenu';

/** Optional aggregation metadata; rendered as a `× N` pill when count > 1. */
export type ListRow = LogLine & { count?: number; lastTs?: string };

/* -------------------------------------------------------------------- */
/* Tunable smoothness / sticky parameters (top-level for easy tweaking) */
/* -------------------------------------------------------------------- */

/** Fraction of the remaining distance to scroll each frame (exponential ease-out). */
const LERP_ALPHA = 0.18;
/** Hard cap on per-frame movement (≈ 30 * 60Hz ≈ 1800 px/s). */
const MAX_STEP_PX = 30;
/** Stop the rAF loop when we're closer than this to the target. */
const SETTLE_PX = 0.5;
/** Distance from bottom (px) at which the list considers itself "stuck to tail". */
const STICKY_THRESHOLD_PX = 50;
/** Window after a programmatic scrollTop write during which we ignore native scroll events. */
const PROGRAMMATIC_GUARD_MS = 100;
/** Pulse highlight duration for `pulseIndex`. */
const PULSE_DURATION_MS = 1500;
/**
 * After a user-initiated scroll-affecting input (wheel/touch/drag/key), the
 * tail-following lerp must not write to scrollTop for this many ms. Long
 * enough to clearly hand control to the user; short enough that on the next
 * deliberate "go to bottom" they can immediately resume sticky.
 */
const USER_COOLDOWN_MS = 600;
/** Fixed row height in virtualized mode. */
const ITEM_SIZE = 18;
/**
 * wrap 模式下未量过的行的占位高度估值（单行 wrap-pre 文本约 24px；
 * 多数日志行只有一两行内容）。VariableSizeList 用这个值给未渲染过的
 * 离屏行先占位，渲染后 ResizeObserver 量出真实高度再回写缓存，避免
 * 滚动条尺寸跟用户实际看到的内容偏离太多。
 */
const WRAP_DEFAULT_ITEM_SIZE = 24;
/**
 * If the lerp target is further than this multiple of the viewport height,
 * treat it as a layout jump (toggle wrap / aggregate / unfreeze / first
 * mount on a deep tail) and snap instantly. Animating across thousands of
 * pixels would otherwise visibly crawl the list from the middle to the
 * bottom for 2-3 seconds.
 */
const BIG_JUMP_VIEWPORT_MULT = 5;

/* -------------------------------------------------------------------- */
/* Types                                                                */
/* -------------------------------------------------------------------- */

interface VirtualListHandle {
  scrollToItem: (index: number, align?: 'start' | 'center' | 'end' | 'auto') => void;
  scrollTo: (offset: number) => void;
}

/**
 * Bundle every per-row prop the renderer needs. We pass this through
 * `itemData` so the renderer component itself (`FlatRow` below) can stay
 * referentially stable — react-window calls `React.createElement(children, …)`
 * which means a fresh inline render-prop would be a brand new component
 * type each render, forcing react to UNMOUNT every visible row's DOM. That
 * unmount/remount race is what makes the first token click in a fresh tab
 * silently fail: between the user's mousedown and click, the streaming
 * re-render replaces the button DOM, so the click never lands.
 */
interface RowData {
  lines: ListRow[];
  pulseIdx: number | null;
  selectedIndex: number | null;
  searchLower: string;
  onTokenClick?: (field: TokenField, value: string, rect: AnchorRect) => void;
  onRowClick: (index: number, e: React.MouseEvent) => void;
  /**
   * 行内 marker 计算函数。返回 null 时不渲染 marker；返回值带 color 与
   * tooltip。Phase 14 日志检查点引擎在 LogList 上叠加规则命中色条。
   */
  markerFor?: (line: ListRow) => { color: string; title: string } | null;
  onRowContextMenu?: (line: ListRow, rect: AnchorRect) => boolean | void;
  /**
   * Phase 14.1 多选支持：传入 Set 让该行带"已多选"边条样式；
   * 父组件通过 onRowClick 的 e.shiftKey / e.metaKey 推断多选意图。
   */
  multiSelected?: Set<number>;
  /**
   * wrap 模式专用：变长虚拟行 mount / 内容变高时回写实际像素高度，让
   * VariableSizeList 可以重算偏移。FixedSize 分支不会传这个回调。
   */
  setWrapRowHeight?: (index: number, height: number) => void;
}




type RWProps = {
  height: number;
  itemCount: number;
  itemSize: number;
  width: number | string;
  outerRef?: React.Ref<HTMLDivElement>;
  itemData?: RowData;
  /**
   * react-window 把这个值原样设置到 outer 滚动容器上。我们用它把
   * `font-mono text-[11px]` 这类容器级字体/背景类放在外层 div 上，
   * 让所有子行 CSS 继承——跟原始的 `<div className="...">{lines.map}</div>`
   * 行为一致，避免每行重复声明字体类。
   */
  className?: string;
  /**
   * react-window's `overscanCount` defaults to 2. With 18px rows that's
   * ≈36px of pre-rendered headroom — wholly insufficient when the user
   * drags the scrollbar or flings the wheel. Their viewport scrolls
   * faster than the next render commit, and rows pop in as transparent
   * gaps at the top/bottom edges. We bumped this to 100 (~1800px ≈ a
   * couple of viewports each side) after a 40-row baseline still left
   * visible holes during aggressive scrollbar drags on tabs whose
   * `displayRows` had grown into the tens of thousands (filtered tabs
   * with MATCH_RING ≈ 100k). 200+ rendered rows is fine cost-wise —
   * every row is `React.memo`'d and DOM is recycled by react-window.
   */
  overscanCount?: number;
  children: React.ComponentType<{
    index: number;
    style: React.CSSProperties;
    data: RowData;
  }>;
};

/** wrap 分支用的变长 prop 形态（itemSize 是 (index)=>number）。 */
type RWVariableProps = Omit<RWProps, 'itemSize'> & {
  itemSize: (index: number) => number;
  estimatedItemSize?: number;
};

interface VariableListHandle extends VirtualListHandle {
  /** 从 index 起重算行偏移；shouldForceUpdate=false 仅 invalidate 缓存。 */
  resetAfterIndex: (index: number, shouldForceUpdate?: boolean) => void;
}

const VirtualList = FixedSizeList as unknown as React.ComponentType<
  RWProps & React.RefAttributes<VirtualListHandle>
>;

const VariableList = VariableSizeList as unknown as React.ComponentType<
  RWVariableProps & React.RefAttributes<VariableListHandle>
>;

export type TokenField = 'tag' | 'pid' | 'level';

export interface LogListHandle {
  /**
   * Scroll to the row at `index`. Cancels active tail-following.
   * `smooth=true` (default) uses the native smooth scroll animation;
   * `smooth=false` snaps instantly — used by the toolbar "跳到顶部"
   * button where the user wants an immediate jump.
   */
  scrollToIndex: (index: number, align?: 'start' | 'center' | 'end', smooth?: boolean) => void;
  /** Scroll back to the latest row + resume sticky. `smooth=false` snaps. */
  scrollToBottom: (smooth?: boolean) => void;
  /** Briefly highlight a row (1.5s pulse) — used after jump-to. */
  pulseIndex: (index: number) => void;
  /** Whether the list is currently sticking to the tail. */
  isSticky: () => boolean;
}

interface Props {
  lines: ListRow[];
  wrap: boolean;
  /**
   * User-intended "follow tail" flag. Effective sticky also requires the
   * viewport to be near the bottom. The list will silently lose sticky when
   * the user scrolls up, regardless of this flag.
   */
  autoScroll: boolean;
  height: number;
  onStickyChange?: (sticky: boolean) => void;
  onPendingNewChange?: (n: number) => void;
  /** Click handler for tag/pid/level tokens; parent shows the popover. */
  onTokenClick?: (field: TokenField, value: string, rect: AnchorRect) => void;
  /** Optional case-insensitive substring to highlight inside each row. */
  searchQuery?: string;
  /**
   * Controlled "pinned" selection: when set, this row gets the emerald
   * highlight and auto-tail is suppressed entirely. Parent owns the
   * state so it can render a banner / Esc shortcut / clear button.
   */
  selectedIndex?: number | null;
  /** Toggle / set / clear pinned row from inside the list. */
  onSelectChange?: (index: number | null) => void;
  /**
   * Phase 14 检查点 marker：把每行交给宿主返回一个 marker（颜色 +
   * tooltip）；返回 null 表示该行没有任何命中。LogList 在每行最左
   * 侧再渲染一道 2px 色条，视觉与 selected 状态共存（selected 仍
   * 用 emerald 1px 内边）。
   */
  markerFor?: (line: ListRow) => { color: string; title: string } | null;
  /**
   * Phase 14.1 多选行集合（display row 索引）。Shift/Cmd+Click 进入多
   * 选；普通点击仍走 pin 单选。父组件用此 Set 渲染浮动 ActionBar +
   * 把多选行交给 LineToRuleModal multi-line 模式。
   */
  multiSelected?: Set<number>;
  /** 多选变化回调；undefined 时禁用多选。 */
  onMultiSelectChange?: (next: Set<number>) => void;
  /**
   * 行右键菜单回调。Logs 页面用它弹出"转为检查规则"。返回 false /
   * 不传时按浏览器默认右键菜单（保留 copy 等功能）。
   */
  onRowContextMenu?: (line: ListRow, rect: AnchorRect) => boolean | void;
}

/* -------------------------------------------------------------------- */
/* Component                                                            */
/* -------------------------------------------------------------------- */

export const LogList = forwardRef<LogListHandle, Props>(function LogList(
  {
    lines,
    wrap,
    autoScroll,
    height,
    onStickyChange,
    onPendingNewChange,
    onTokenClick,
    searchQuery,
    selectedIndex = null,
    onSelectChange,
    markerFor,
    onRowContextMenu,
    multiSelected,
    onMultiSelectChange,
  },
  ref
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const virtualRef = useRef<VirtualListHandle>(null);
  const variableRef = useRef<VariableListHandle>(null);

  /**
   * wrap 模式行高缓存。**按 line.id 而非 index** 索引：ring evict / 过滤
   * 切换会让 index 整体移位，按 id 存就不用每次 shift；只有 id 不存在
   * （历史回放残留）才回退用 index。
   */
  const wrapHeightsByIdRef = useRef<Map<number, number>>(new Map());
  const wrapHeightsByIdxRef = useRef<Map<number, number>>(new Map());

  /** lines 的 ref 镜像，让 getWrapItemSize 保持稳定身份。 */
  const linesRef = useRef<ListRow[]>(lines);
  linesRef.current = lines;
  /** 上次 lines 形态的快照：长度 + head[0].id，用于决定是否要 reset 偏移。 */
  const wrapPrevSigRef = useRef<{ len: number; firstId: number | undefined }>({
    len: 0,
    firstId: undefined,
  });

  /**
   * 给变长列表的稳定 itemSize getter。引用永远不变，避免 VariableSizeList
   * 把整个 prop 当变化重建。
   */
  const getWrapItemSize = useCallback((index: number): number => {
    const line = linesRef.current[index];
    if (!line) return WRAP_DEFAULT_ITEM_SIZE;
    if (line.id !== undefined) {
      const cached = wrapHeightsByIdRef.current.get(line.id);
      if (cached !== undefined) return cached;
    }
    const idxCached = wrapHeightsByIdxRef.current.get(index);
    if (idxCached !== undefined) return idxCached;
    return WRAP_DEFAULT_ITEM_SIZE;
  }, []);

  /**
   * 一帧内合并多行高度变化的脏标记：minIdx 是本帧需要 resetAfterIndex
   * 的最小 index（react-window 只需要从最小脏点起重算偏移就够了）；
   * raf 是已 schedule 的 frame 句柄，避免一帧排多次。
   *
   * 没有这层合并时，首屏挂载一屏 ~50 行各自的 ResizeObserver 在下一帧
   * 异步 fire，每次都同步触发 `resetAfterIndex` → `VariableSizeList.
   * forceUpdate` → 整窗 child 重渲染，新挂载的行又走一遍同样的环。
   * 10Hz 的 stream chunk 节奏下会把主线程钉死。
   */
  const wrapDirtyRef = useRef<{ minIdx: number; raf: number | null }>({
    minIdx: Number.POSITIVE_INFINITY,
    raf: null,
  });

  /**
   * 回写一行的实际像素高度。同样按 id 主存；id 缺失时退回用 index。
   * 仅在确实变化时把 index 收入 dirty set 并 schedule rAF，让一帧最多
   * 触发一次 `resetAfterIndex`，把无虚拟化时代的「每行一次 forceUpdate」
   * 压成「每帧最多一次 forceUpdate」。
   */
  const setWrapRowHeight = useCallback((index: number, height: number) => {
    if (height <= 0) return;
    const h = Math.round(height);
    const line = linesRef.current[index];
    if (line?.id !== undefined) {
      if (wrapHeightsByIdRef.current.get(line.id) === h) return;
      wrapHeightsByIdRef.current.set(line.id, h);
    } else {
      if (wrapHeightsByIdxRef.current.get(index) === h) return;
      wrapHeightsByIdxRef.current.set(index, h);
    }
    const dirty = wrapDirtyRef.current;
    if (index < dirty.minIdx) dirty.minIdx = index;
    if (dirty.raf !== null) return;
    dirty.raf = requestAnimationFrame(() => {
      const minIdx = dirty.minIdx;
      dirty.minIdx = Number.POSITIVE_INFINITY;
      dirty.raf = null;
      if (Number.isFinite(minIdx)) {
        variableRef.current?.resetAfterIndex(minIdx, false);
      }
    });
  }, []);

  const stickyRef = useRef(true);
  const [sticky, setSticky] = useState(true);
  /**
   * Persistent "pinned" selection from props. The auto-tail follower
   * checks this ref every frame; setting a non-null value freezes the
   * viewport at that row regardless of streaming.
   */
  const selectedIndexRef = useRef<number | null>(selectedIndex);
  selectedIndexRef.current = selectedIndex;
  const programmaticUntilRef = useRef(0);
  /**
   * Last time the user produced a scroll-affecting input (wheel, touch drag,
   * mouse drag on scrollbar, keyboard PgUp/PgDn etc.). Within `USER_COOLDOWN_MS`
   * after this timestamp we refuse to issue any `writeScrollTop` from the
   * tail-following lerp. This is what fixes "scrollbar in the middle but
   * content keeps scrolling" — the lerp was previously stomping over user
   * input because every lerp frame extended `programmaticUntilRef` and the
   * scroll handler ignored the user's events entirely.
   */
  const userInteractRef = useRef(0);
  const targetTopRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const prevLenRef = useRef(0);
  const pendingNewRef = useRef(0);
  const [pulseIdx, setPulseIdx] = useState<number | null>(null);
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Stable cb refs so the scroll listener doesn't go stale. */
  const onStickyChangeRef = useRef(onStickyChange);
  const onPendingNewChangeRef = useRef(onPendingNewChange);
  useEffect(() => {
    onStickyChangeRef.current = onStickyChange;
    onPendingNewChangeRef.current = onPendingNewChange;
  });

  const itemCount = lines.length;
  // 给 scheduleFollowToEnd / 其他闭包稳定读取最新 itemCount 用：避免
  // 把 itemCount 加进 scheduleFollowToEnd 的 deps 后，每次 chunk 都让
  // 依赖它的 effect (selectionchange / autoScroll effect / itemCount
  // effect) 整套重 bind。
  const itemCountRef = useRef(itemCount);
  itemCountRef.current = itemCount;

  /* ------------------------- internal helpers --------------------------- */

  /** Does the user currently have a non-collapsed text selection inside
   * the log container? If so we must NOT touch scrollTop or we'll wipe
   * the selection (browsers drop selections on layout-changing writes). */
  const hasUserSelectionIn = useCallback((el: HTMLElement | null): boolean => {
    if (!el) return false;
    const sel = typeof window !== 'undefined' ? window.getSelection() : null;
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) return false;
    const r = sel.getRangeAt(0);
    return el.contains(r.startContainer) || el.contains(r.endContainer);
  }, []);

  const writeScrollTop = useCallback((top: number) => {
    const el = containerRef.current;
    if (!el) return;
    // Honour the user-cooldown window: a lerp frame must NOT push scrollTop
    // back to its target while the user is actively dragging the scrollbar,
    // spinning the wheel, or holding PgDn. We do still touch scrollTop for
    // imperative APIs (scrollToBottom / scrollToIndex) — those reset
    // `userInteractRef` to 0 before calling us.
    if (performance.now() - userInteractRef.current < USER_COOLDOWN_MS) return;
    programmaticUntilRef.current = performance.now() + PROGRAMMATIC_GUARD_MS;
    el.scrollTop = top;
  }, []);

  const tick = useCallback(() => {
    rafRef.current = null;
    const el = containerRef.current;
    if (!el) return;
    // Hold off while the user is selecting text — touching scrollTop
    // would obliterate the selection. We keep `targetTopRef` so the
    // moment the selection clears, the next `scheduleFollowToEnd` resumes.
    if (hasUserSelectionIn(el)) return;
    const diff = targetTopRef.current - el.scrollTop;
    if (Math.abs(diff) < SETTLE_PX) {
      writeScrollTop(targetTopRef.current);
      return;
    }
    // Big-jump cutoff: don't crawl thousands of px at 1800 px/s — snap.
    if (Math.abs(diff) > el.clientHeight * BIG_JUMP_VIEWPORT_MULT) {
      writeScrollTop(targetTopRef.current);
      return;
    }
    const step = Math.sign(diff) * Math.min(Math.abs(diff) * LERP_ALPHA, MAX_STEP_PX);
    writeScrollTop(el.scrollTop + step);
    rafRef.current = requestAnimationFrame(tick);
  }, [writeScrollTop, hasUserSelectionIn]);

  const scheduleFollowToEnd = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (hasUserSelectionIn(el)) return; // selection-protected, don't move
    if (selectedIndexRef.current !== null) return; // pinned row → don't auto-scroll
    if (performance.now() - userInteractRef.current < USER_COOLDOWN_MS) return;
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    // wrap 模式下走 react-window 自身的 scrollToItem(last, 'end')：
    // 变长虚拟列表里 el.scrollHeight 是 (已测量行真高 + 未测量行 ×
    // estimatedItemSize) 估算值，10k 行级别下偏差能到几千 px。直接拿
    // 这个值算 scrollTop 追尾，每个 chunk 都"贴底但其实不到底"，react-
    // window 重算 visible window → 挂载新行 → ResizeObserver → resetAfter
    // Index → forceUpdate → scrollHeight 又变 → 下个 chunk 再追，构成
    // 10Hz × 每次 50+ 行的反馈风暴。
    // scrollToItem('end') 内部按已知 itemSize 累积偏移并保证目标行落在
    // 视口底端，没有"估算偏差再纠正"这一步，能从根上断掉循环。
    if (wrap) {
      const last = itemCountRef.current - 1;
      if (last < 0) return;
      programmaticUntilRef.current = performance.now() + PROGRAMMATIC_GUARD_MS;
      variableRef.current?.scrollToItem(last, 'end');
      // 把 targetTop 同步到当前 scrollTop，避免遗留 lerp 把视口拉走。
      targetTopRef.current = containerRef.current?.scrollTop ?? 0;
      return;
    }
    // AS-style stability: teleport scrollTop to the tail on every tick
    // instead of lerping toward it. The old lerp animated at 30 px/frame
    // ≈ 1800 px/s ≈ 100 行/s — anything faster looked like the viewport
    // was perpetually "chasing" logs across the screen. Snapping pins
    // the bottom of the buffer optically stable even at 1000+ 行/s,
    // matching what AS / DDMS do. The lerp infrastructure is kept for
    // explicit smooth jumps (scrollToIndex(smooth=true)) which use the
    // browser's native `scrollTo({behavior:'smooth'})`, not our lerp.
    const target = Math.max(0, el.scrollHeight - el.clientHeight);
    targetTopRef.current = target;
    programmaticUntilRef.current = performance.now() + PROGRAMMATIC_GUARD_MS;
    el.scrollTop = target;
  }, [wrap, hasUserSelectionIn]);

  const cancelLerp = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const handleScrollNative = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    // `programmaticUntilRef` is *only* used to filter out the synchronous
    // scroll event echo from our own writeScrollTop (~next frame). Crucially
    // we don't extend it on every lerp frame anymore, so the user's events
    // can still update sticky.
    if (performance.now() < programmaticUntilRef.current) return;
    // 这是用户驱动的 scroll（非 programmatic guard 内）。每次都把
    // `userInteractRef` 刷新到现在，让 `scheduleFollowToEnd` 的 cooldown
    // 在整条拖动期间持续 alive——否则只有 mousedown 那一下打了 cooldown
    // 标记，超过 USER_COOLDOWN_MS (600ms) 后追尾会"自动倒拖"用户回底，
    // 视觉上日志快速刷屏，无法点行 pin。
    // scrollbar 拖动时浏览器只 fire mousedown 一次 + 连续 scroll，没有
    // 持续的 wheel/touch 事件可以 piggyback——必须在 scroll 路径上刷。
    userInteractRef.current = performance.now();
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = distFromBottom <= STICKY_THRESHOLD_PX;
    if (nearBottom !== stickyRef.current) {
      stickyRef.current = nearBottom;
      setSticky(nearBottom);
      onStickyChangeRef.current?.(nearBottom);
      if (nearBottom) {
        pendingNewRef.current = 0;
        onPendingNewChangeRef.current?.(0);
      }
    }
  }, []);

  /**
   * The user did something that means "I am taking over scrolling".
   * Cancel any in-flight lerp and mark the cooldown window. We deliberately
   * DO NOT call `setSticky(false)` / `onStickyChange(false)` here:
   *
   * - This handler runs on the native `mousedown` event, which the browser
   *   fires *before* React's synthetic `click` for the same gesture. In
   *   React 18, a synchronous `setState` inside that mousedown bridge
   *   triggers a commit that lands between mousedown and click — which
   *   silently swallows the upcoming click (TokenMenu never opens on the
   *   first try) and replaces row DOM (the browser drops the in-progress
   *   text selection).
   * - Sticky is fundamentally a function of scroll position, so let the
   *   real `handleScrollNative` handler flip it once the user actually
   *   scrolls. Until then, the cooldown ref already prevents the lerp
   *   from fighting the user.
   */
  const noteUserInteract = useCallback(() => {
    userInteractRef.current = performance.now();
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  /**
   * Click on a row body (anywhere outside the inline tag/pid/level tokens
   * which have their own popover handlers). Pin that row as the current
   * focus and emit a select-change to the parent. We also flip `sticky`
   * to false because pin freezes the viewport — without that flip, the
   * itemCount effect below would still go through the "follow tail"
   * branch and silently skip `pendingNew` accumulation, leaving the
   * lock banner stuck at "+0" forever. The user wouldn't know if the
   * stream was still alive. Re-clicking the same row unpins.
   */
  const handleRowClick = useCallback(
    (index: number, e: React.MouseEvent) => {
      // Shift/Cmd+Click → 进入多选模式（不动 pin）
      if (onMultiSelectChange && (e.shiftKey || e.metaKey || e.ctrlKey)) {
        const cur = multiSelected ? new Set(multiSelected) : new Set<number>();
        if (e.shiftKey && cur.size > 0) {
          // Shift: 与当前最近端点之间做区间选
          const arr = Array.from(cur);
          const anchor = arr[arr.length - 1];
          const lo = Math.min(anchor, index);
          const hi = Math.max(anchor, index);
          for (let i = lo; i <= hi; i += 1) cur.add(i);
        } else {
          if (cur.has(index)) cur.delete(index);
          else cur.add(index);
        }
        onMultiSelectChange(cur);
        return;
      }
      const cur = selectedIndexRef.current;
      const next = cur === index ? null : index;
      onSelectChange?.(next);
      if (next !== null && stickyRef.current) {
        stickyRef.current = false;
        setSticky(false);
        onStickyChangeRef.current?.(false);
      }
    },
    [onSelectChange, onMultiSelectChange, multiSelected]
  );

  /** Callback ref: attach the native scroll listener whenever the container
   * node changes (covers wrap-mode toggle since the mounted node changes).
   * Also wires up the user-interaction listeners that take precedence over
   * the auto-follow lerp. */
  const setContainer = useCallback(
    (node: HTMLDivElement | null) => {
      const prev = containerRef.current;
      if (prev && prev !== node) {
        prev.removeEventListener('scroll', handleScrollNative);
        prev.removeEventListener('wheel', noteUserInteract);
        prev.removeEventListener('touchstart', noteUserInteract);
        prev.removeEventListener('mousedown', noteUserInteract);
        prev.removeEventListener('keydown', noteUserInteract);
      }
      containerRef.current = node;
      if (node) {
        node.addEventListener('scroll', handleScrollNative, { passive: true });
        node.addEventListener('wheel', noteUserInteract, { passive: true });
        node.addEventListener('touchstart', noteUserInteract, { passive: true });
        node.addEventListener('mousedown', noteUserInteract);
        node.addEventListener('keydown', noteUserInteract);
      }
    },
    [handleScrollNative, noteUserInteract]
  );

  /* ----------- selectionchange: resume follow once selection clears ----- */

  useEffect(() => {
    const onSelChange = () => {
      const el = containerRef.current;
      if (!el) return;
      if (hasUserSelectionIn(el)) return;
      // Selection cleared. If we still want to follow, schedule a catch-up.
      if (stickyRef.current && autoScroll) {
        scheduleFollowToEnd();
      }
    };
    document.addEventListener('selectionchange', onSelChange);
    return () => document.removeEventListener('selectionchange', onSelChange);
  }, [autoScroll, hasUserSelectionIn, scheduleFollowToEnd]);

  /* ---------------------- effects: tail & sticky ------------------------ */

  // wrap 模式下，当 lines 形态变化（ring evict / 过滤切换 / aggregate
  // toggle 让 head[0].id 跳了；或长度收缩）时，VariableSizeList 内部
  // 的 offset 缓存指向旧索引，行间距会乱跳。这里主动 resetAfterIndex(0)
  // 让它从头按 getWrapItemSize 重新累积偏移。
  // 纯尾部 append（length 增 + head[0].id 不变）不需要 reset，append 的
  // 新行 react-window 会自然查 itemSize 排好。
  useEffect(() => {
    if (!wrap) return;
    const sig = wrapPrevSigRef.current;
    const firstId = lines[0]?.id;
    const len = lines.length;
    const headChanged = sig.firstId !== firstId;
    const shrunk = len < sig.len;
    wrapPrevSigRef.current = { len, firstId };
    if (headChanged || shrunk) {
      variableRef.current?.resetAfterIndex(0, false);
    }
  }, [wrap, lines]);

  // When autoScroll intent flips on, re-arm sticky and start chasing tail.
  useEffect(() => {
    if (autoScroll) {
      stickyRef.current = true;
      setSticky(true);
      pendingNewRef.current = 0;
      onPendingNewChangeRef.current?.(0);
      onStickyChangeRef.current?.(true);
      scheduleFollowToEnd();
    }
  }, [autoScroll, scheduleFollowToEnd]);

  // React to length change: follow tail if growing & sticky, accumulate
  // backlog otherwise. Pinned rows always accumulate so the lock banner
  // surfaces "+N new lines"。
  //
  // 当 itemCount 减少（上游 ring buffer evict / 过滤切换收缩 / 视图切换）
  // 时，react-window FixedSizeList 不会自动调整 scrollTop，导致上方那块
  // ITEM_SIZE × removed 高度的内容"消失"，视觉上整列向上跳一段。这里
  // 在 sticky=false 且 wrap=false 时主动把 scrollTop 同步左移 removed *
  // ITEM_SIZE，让 pinned 行（或用户手动停留的中间位置）的 DOM 位置保持
  // 不变。wrap=true 行高不定，无法用 ITEM_SIZE 简单换算，暂不补偿；
  // RING_SIZE 涨到 200k 后 wrap 用户的 evict 频率本就极低，可接受。
  useEffect(() => {
    const delta = itemCount - prevLenRef.current;
    const removed = -delta;
    prevLenRef.current = itemCount;
    if (removed > 0 && !stickyRef.current && !wrap) {
      const el = containerRef.current;
      if (el) {
        programmaticUntilRef.current = performance.now() + PROGRAMMATIC_GUARD_MS;
        el.scrollTop = Math.max(0, el.scrollTop - removed * ITEM_SIZE);
      }
      return;
    }
    const added = delta;
    if (added <= 0) return;
    if (selectedIndexRef.current !== null) {
      pendingNewRef.current += added;
      onPendingNewChangeRef.current?.(pendingNewRef.current);
      return;
    }
    if (stickyRef.current && autoScroll) {
      scheduleFollowToEnd();
    } else if (!stickyRef.current) {
      pendingNewRef.current += added;
      onPendingNewChangeRef.current?.(pendingNewRef.current);
    }
  }, [itemCount, autoScroll, scheduleFollowToEnd, wrap]);

  // Cleanup on unmount.
  useEffect(
    () => () => {
      cancelLerp();
      if (pulseTimerRef.current !== null) clearTimeout(pulseTimerRef.current);
      const dirty = wrapDirtyRef.current;
      if (dirty.raf !== null) {
        cancelAnimationFrame(dirty.raf);
        dirty.raf = null;
      }
    },
    [cancelLerp]
  );

  /* ------------------------- imperative API ----------------------------- */

  useImperativeHandle(
    ref,
    () => ({
      scrollToIndex: (index, align = 'center', smooth = true) => {
        const el = containerRef.current;
        if (!el) return;
        cancelLerp();
        // Imperative jumps reset the cooldown — they ARE the user's intent.
        userInteractRef.current = 0;
        if (wrap) {
          // 虚拟化后 wrap 模式下子节点是稀疏的（只有视口内行被挂出），
          // 不能再读 `el.children[index].offsetTop`。直接走 react-window
          // 的 scrollToItem，它会按当前 itemSize 累加偏移，再触发列表
          // 重算，让目标行变成实际挂出来的可见行。
          // 没有 smooth 选项是 react-window 的限制；wrap 行高不一致，
          // 平滑滚动距离也很难估准，snap 体验更稳。
          programmaticUntilRef.current =
            performance.now() + (smooth ? 800 : PROGRAMMATIC_GUARD_MS);
          variableRef.current?.scrollToItem(index, align);
          stickyRef.current = false;
          setSticky(false);
          onStickyChangeRef.current?.(false);
          return;
        }
        let target = 0;
        const top = index * ITEM_SIZE;
        if (align === 'start') target = top;
        else if (align === 'end') target = top - el.clientHeight + ITEM_SIZE;
        else target = top - el.clientHeight / 2 + ITEM_SIZE / 2;
        target = Math.max(0, Math.min(target, el.scrollHeight - el.clientHeight));
        if (smooth) {
          programmaticUntilRef.current = performance.now() + 800;
          el.scrollTo({ top: target, behavior: 'smooth' });
        } else {
          // Snap path — only suppress the immediate echo of our own write.
          programmaticUntilRef.current = performance.now() + PROGRAMMATIC_GUARD_MS;
          el.scrollTop = target;
        }
        stickyRef.current = false;
        setSticky(false);
        onStickyChangeRef.current?.(false);
      },
      scrollToBottom: (smooth = true) => {
        const el = containerRef.current;
        if (!el) return;
        cancelLerp();
        userInteractRef.current = 0;
        // Jumping to bottom is an explicit "I'm caught up, follow again"
        // gesture — release any pinned-row lock so streaming resumes.
        if (selectedIndexRef.current !== null) onSelectChange?.(null);
        if (wrap && itemCount > 0) {
          // 变长虚拟列表里 `el.scrollHeight` 受未测量行的估值影响，直接
          // `scrollTo({ top: maxTop })` 多半到不了真正的尾。让 react-window
          // 自己定位最后一行，挂载尾部那一屏后，宿主里的 onScroll 会识别
          // 出 sticky 并继续追尾。
          programmaticUntilRef.current =
            performance.now() + (smooth ? 800 : PROGRAMMATIC_GUARD_MS);
          variableRef.current?.scrollToItem(itemCount - 1, 'end');
        } else {
          const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
          if (smooth) {
            programmaticUntilRef.current = performance.now() + 800;
            el.scrollTo({ top: maxTop, behavior: 'smooth' });
          } else {
            // Bypass the cooldown gate for this one write — caller wants snap.
            programmaticUntilRef.current = performance.now() + PROGRAMMATIC_GUARD_MS;
            el.scrollTop = maxTop;
          }
        }
        stickyRef.current = true;
        setSticky(true);
        pendingNewRef.current = 0;
        onStickyChangeRef.current?.(true);
        onPendingNewChangeRef.current?.(0);
      },
      pulseIndex: (index) => {
        setPulseIdx(index);
        if (pulseTimerRef.current !== null) clearTimeout(pulseTimerRef.current);
        pulseTimerRef.current = setTimeout(() => setPulseIdx(null), PULSE_DURATION_MS);
      },
      isSticky: () => stickyRef.current,
    }),
    [wrap, itemCount, cancelLerp, onSelectChange]
  );

  /* --------------------------- rendering -------------------------------- */
  void sticky;

  const searchLower = useMemo(
    () => (searchQuery && searchQuery.length > 0 ? searchQuery.toLowerCase() : ''),
    [searchQuery]
  );

  // Bundle per-row state for react-window's `itemData`. The bundle's
  // identity changes whenever any of these change, but the render
  // component (`VirtualRow`) below is module-level and never changes —
  // so row DOM is preserved across renders and the mousedown→click
  // sequence on a token button stays intact.
  const itemData: RowData = useMemo(
    () => ({
      lines,
      pulseIdx,
      selectedIndex,
      searchLower,
      onTokenClick,
      onRowClick: handleRowClick,
      markerFor,
      onRowContextMenu,
      multiSelected,
      // setWrapRowHeight 是空依赖 useCallback，引用永远稳定，
      // 作为 itemData 字段不会击穿父级 memo。
      setWrapRowHeight,
    }),
    [
      lines,
      pulseIdx,
      selectedIndex,
      searchLower,
      onTokenClick,
      handleRowClick,
      markerFor,
      onRowContextMenu,
      multiSelected,
      setWrapRowHeight,
    ]
  );

  if (wrap) {
    return (
      <VariableList
        ref={variableRef}
        outerRef={setContainer as unknown as React.Ref<HTMLDivElement>}
        height={height}
        itemCount={itemCount}
        itemSize={getWrapItemSize}
        estimatedItemSize={WRAP_DEFAULT_ITEM_SIZE}
        width="100%"
        itemData={itemData}
        // 容器级字体/背景类。原始 wrap 分支是
        // `<div className="overflow-auto bg-bg-card font-mono text-[11px] leading-snug">`
        // 把字体类放在容器上，子行通过 CSS 继承。改成 VariableSizeList 后
        // outer div 没有这些类，子行字号回到浏览器默认（≈16px），视觉上
        // "字体明显变大"。这里通过 react-window 的 className prop 把同样
        // 的容器类透传到它生成的 outer div，保持视觉一致。
        // `overflow-auto` 由 react-window 自身设置（它会写 overflow: auto
        // 到 outerStyle），不用我们再加。
        className="bg-bg-card font-mono text-[11px] leading-snug"
        // wrap 行真实高度差异更大、估值偏差也更大，overscan 取一个能
        // 在快速 wheel/scrollbar 拖动下盖住一屏左右的值（≈一屏 30 行 →
        // 各 30 行 buffer），同时不至于把 reconciler 拉满。
        overscanCount={30}
      >
        {VirtualWrapRow}
      </VariableList>
    );
  }

  return (
    <VirtualList
      ref={virtualRef}
      outerRef={setContainer as unknown as React.Ref<HTMLDivElement>}
      height={height}
      itemCount={itemCount}
      itemSize={ITEM_SIZE}
      width="100%"
      itemData={itemData}
      overscanCount={100}
    >
      {VirtualRow}
    </VirtualList>
  );
});

/**
 * Module-level row renderer used as react-window's `children`. Stable
 * identity is mandatory: react-window does `createElement(children, …)`
 * so a new function each parent render would be treated as a new
 * component type and **unmount every visible row's DOM**. That race
 * between unmount/mount and a user's click is why a fresh tab used to
 * eat the first token click.
 */
function VirtualRow({
  index,
  style,
  data,
}: {
  index: number;
  style: React.CSSProperties;
  data: RowData;
}) {
  return (
    <MemoFlatRow
      style={style}
      line={data.lines[index]}
      index={index}
      pulse={data.pulseIdx === index}
      selected={data.selectedIndex === index}
      multiPicked={data.multiSelected ? data.multiSelected.has(index) : false}
      searchLower={data.searchLower}
      onTokenClick={data.onTokenClick}
      onRowClick={data.onRowClick}
      marker={data.markerFor ? data.markerFor(data.lines[index]) ?? null : null}
      onRowContextMenu={data.onRowContextMenu}
    />
  );
}

/* -------------------------------------------------------------------- */
/* Rows                                                                 */
/* -------------------------------------------------------------------- */

interface RowProps {
  line: ListRow;
  style?: React.CSSProperties;
  index: number;
  pulse: boolean;
  selected: boolean;
  /** Phase 14.1：是否在多选 Set 内（额外蓝色边条）。 */
  multiPicked?: boolean;
  searchLower: string;
  onTokenClick?: (field: TokenField, value: string, rect: AnchorRect) => void;
  onRowClick?: (index: number, e: React.MouseEvent) => void;
  /** Phase 14：行内 marker，null 时不渲染。 */
  marker?: { color: string; title: string } | null;
  onRowContextMenu?: (line: ListRow, rect: AnchorRect) => boolean | void;
}

function FlatRow({
  line,
  style,
  index,
  pulse,
  selected,
  multiPicked,
  searchLower,
  onTokenClick,
  onRowClick,
  marker,
  onRowContextMenu,
}: RowProps) {
  const lvCls = levelClass(line.level);
  return (
    <div
      style={style}
      data-line-index={index}
      className={clsx(
        'group whitespace-pre px-2 font-mono text-[11px] leading-[18px] select-text relative cursor-pointer',
        // Selected pin: bold emerald left bar + 25% bg + inset ring make
        // it unmistakable against the dark theme.
        selected
          ? 'bg-emerald-500/25 ring-1 ring-inset ring-emerald-500/40 before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-emerald-500'
          : multiPicked
            ? 'bg-sky-500/15 ring-1 ring-inset ring-sky-500/40 before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-sky-500'
            : 'hover:bg-bg-soft',
        pulse && 'animate-[pulse-row_1.5s_ease-out] bg-amber-500/20'
      )}
      title={marker ? marker.title : line.raw}
      onClick={(e) => {
        // Ignore clicks that originated on the tag/pid/level token buttons
        // (those have their own popover). We only treat clicks on the
        // row "body" (timestamps, message text, blank gaps) as pin.
        if ((e.target as HTMLElement).closest('button')) return;
        onRowClick?.(index, e);
      }}
      onContextMenu={
        onRowContextMenu
          ? (e) => {
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              const handled = onRowContextMenu(line, {
                top: rect.top,
                bottom: rect.bottom,
                left: e.clientX,
                right: e.clientX,
              });
              if (handled) e.preventDefault();
            }
          : undefined
      }
    >
      {marker && !selected && (
        <span
          aria-hidden
          className="pointer-events-none absolute right-0 top-0 h-full w-[3px]"
          style={{ backgroundColor: marker.color }}
          title={marker.title}
        />
      )}
      <RowContent
        line={line}
        lvCls={lvCls}
        searchLower={searchLower}
        onTokenClick={onTokenClick}
      />
    </div>
  );
}

/**
 * wrap 行容器隔离样式：只用 `contain: layout style`，不再用
 * `content-visibility: auto`。
 *
 * 早期为了在没虚拟化的 wrap 模式里压低 layout/paint 成本，给每行加了
 * `content-visibility: auto`，但这跟 `VirtualWrapRow` 的 ResizeObserver
 * 自测高度路径会冲突——离屏（含 react-window 的 overscan）的行会被
 * 浏览器跳过 layout，回报的尺寸是 `containIntrinsicSize` 的占位值
 * (24px)，把高度缓存写错，滚动条乱跳。
 *
 * 现在 wrap 模式已经走 `VariableSizeList` 真正虚拟化（只挂载视口附近
 * 的几十行），CV 的"裁剪离屏"作用被替代；保留 `contain: layout style`
 * 是为了让某行内容的 reflow 不会冒泡到祖先列表。
 */
const WRAP_ROW_CONTAIN_STYLE: React.CSSProperties = {
  contain: 'layout style',
};

function WrapRow({
  line,
  index,
  pulse,
  selected,
  multiPicked,
  searchLower,
  onTokenClick,
  onRowClick,
  marker,
  onRowContextMenu,
}: RowProps) {
  const lvCls = levelClass(line.level);
  return (
    <div
      data-line-index={index}
      className={clsx(
        'relative whitespace-pre-wrap break-all px-2 py-0.5 select-text cursor-pointer',
        selected
          ? 'bg-emerald-500/25 ring-1 ring-inset ring-emerald-500/40 before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-emerald-500'
          : multiPicked
            ? 'bg-sky-500/15 ring-1 ring-inset ring-sky-500/40 before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-sky-500'
            : 'hover:bg-bg-soft',
        pulse && 'animate-[pulse-row_1.5s_ease-out] bg-amber-500/20'
      )}
      style={WRAP_ROW_CONTAIN_STYLE}
      title={marker ? marker.title : undefined}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        onRowClick?.(index, e);
      }}
      onContextMenu={
        onRowContextMenu
          ? (e) => {
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              const handled = onRowContextMenu(line, {
                top: rect.top,
                bottom: rect.bottom,
                left: e.clientX,
                right: e.clientX,
              });
              if (handled) e.preventDefault();
            }
          : undefined
      }
    >
      {marker && !selected && (
        <span
          aria-hidden
          className="pointer-events-none absolute right-0 top-0 h-full w-[3px]"
          style={{ backgroundColor: marker.color }}
          title={marker.title}
        />
      )}
      <RowContent
        line={line}
        lvCls={lvCls}
        searchLower={searchLower}
        onTokenClick={onTokenClick}
      />
    </div>
  );
}

/**
 * Memo wrapper: bail out unless the row's identity, pulse state, selection,
 * or search highlight changed. Combined with `aggregateConsecutive`'s
 * stable-identity reuse, this keeps row DOM stable across stream ticks
 * — required for the browser to preserve text selection.
 */
function rowPropsEqual(a: RowProps, b: RowProps): boolean {
  // marker：浅比较 color+title，避免每行都重新生成对象击穿 memo
  const aM = a.marker;
  const bM = b.marker;
  const markerEq =
    aM === bM ||
    (!!aM && !!bM && aM.color === bM.color && aM.title === bM.title);
  return (
    a.line === b.line &&
    a.index === b.index &&
    a.pulse === b.pulse &&
    a.selected === b.selected &&
    a.multiPicked === b.multiPicked &&
    a.searchLower === b.searchLower &&
    a.onTokenClick === b.onTokenClick &&
    a.onRowClick === b.onRowClick &&
    a.onRowContextMenu === b.onRowContextMenu &&
    markerEq
  );
}

const MemoFlatRow = memo(FlatRow, rowPropsEqual);
const MemoWrapRow = memo(WrapRow, rowPropsEqual);

/**
 * wrap 模式下的虚拟行外壳。挂在 `VariableSizeList` 下，吃 react-window
 * 算好的 `style`（绝对定位 + top + height），里面再 mount `MemoWrapRow`
 * 渲染真正的内容。
 *
 * 自测高度策略：内层 div 用 `useLayoutEffect` + `ResizeObserver`，每次
 * 内容尺寸变化（首次 mount、字体回填、token 数量变化、过滤匹配收/放
 * 等）就回调 `data.setWrapRowHeight`，由父组件去 `resetAfterIndex` 把
 * 滚动条尺寸跟实际高度对齐。
 *
 * 没虚拟化前 wrap 模式是 `lines.map` 一把渲染 1w 行 React 元素，每次
 * 点击都要走完 1w 次 `memo` 比较 + reconciler diff，主线程被 React 自
 * 己 pin 在 100~300ms 上。换 VariableSizeList 后任意时刻只挂载视口内
 * 的 ~30~80 行，点击/选中只会触发常数级别的 reconcile。
 */
function VirtualWrapRow({
  index,
  style,
  data,
}: {
  index: number;
  style: React.CSSProperties;
  data: RowData;
}) {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const { setWrapRowHeight: setHeight } = data;

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el || !setHeight) return;
    setHeight(index, el.getBoundingClientRect().height);
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      // 优先使用 borderBoxSize（含 padding/border），FF/Safari 老版本
      // 没有该字段时回退到 contentRect。
      const box = entry.borderBoxSize?.[0];
      const h = box ? box.blockSize : entry.contentRect.height;
      if (h > 0) setHeight(index, h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [index, setHeight]);

  const line = data.lines[index];
  if (!line) {
    return <div style={style} />;
  }

  return (
    <div style={style}>
      <div ref={innerRef}>
        <MemoWrapRow
          line={line}
          index={index}
          pulse={data.pulseIdx === index}
          selected={data.selectedIndex === index}
          multiPicked={data.multiSelected ? data.multiSelected.has(index) : false}
          searchLower={data.searchLower}
          onTokenClick={data.onTokenClick}
          onRowClick={data.onRowClick}
          marker={data.markerFor ? data.markerFor(line) ?? null : null}
          onRowContextMenu={data.onRowContextMenu}
        />
      </div>
    </div>
  );
}

function RowContent({
  line,
  lvCls,
  searchLower,
  onTokenClick,
}: {
  line: ListRow;
  lvCls: string;
  searchLower: string;
  onTokenClick?: (field: TokenField, value: string, rect: AnchorRect) => void;
}) {
  const count = line.count ?? 1;
  const dupBadge =
    count > 1 ? (
      <span
        className="ml-1.5 inline-block rounded bg-amber-500/20 px-1 font-mono text-[10px] font-semibold text-amber-500"
        title={
          line.lastTs && line.lastTs !== line.ts
            ? `最近一次出现：${line.lastTs}`
            : `重复 ${count} 次`
        }
      >
        × {count.toLocaleString()}
      </span>
    ) : null;
  if (!line.ts) {
    return (
      <span className="text-ink-dim">
        {highlight(line.raw, searchLower)}
        {dupBadge}
      </span>
    );
  }
  const handle = (field: TokenField, value: string) =>
    onTokenClick
      ? (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          // Snapshot the rect at click time; the DOM node may be recycled
          // by react-window before the menu mounts.
          const r = e.currentTarget.getBoundingClientRect();
          onTokenClick(field, value, {
            top: r.top,
            bottom: r.bottom,
            left: r.left,
            right: r.right,
          });
        }
      : undefined;
  return (
    <>
      <span className="text-ink-dim">{line.ts}</span>{' '}
      <button
        type="button"
        className={clsx(
          'text-ink-dim',
          onTokenClick && 'cursor-pointer hover:text-accent hover:underline'
        )}
        onClick={handle('pid', line.pid)}
        disabled={!onTokenClick}
        title={onTokenClick ? `按 PID ${line.pid} 操作` : undefined}
      >
        {line.pid.padStart(5)}
      </button>{' '}
      <span className="text-ink-dim">{line.tid.padStart(5)}</span>{' '}
      <button
        type="button"
        className={clsx(
          'font-bold',
          lvCls,
          onTokenClick && 'cursor-pointer hover:underline'
        )}
        onClick={handle('level', line.level)}
        disabled={!onTokenClick}
        title={onTokenClick ? `按 Level ${line.level} 操作` : undefined}
      >
        {line.level}
      </button>{' '}
      <button
        type="button"
        className={clsx(
          'text-ink',
          onTokenClick && 'cursor-pointer hover:text-accent hover:underline'
        )}
        // 左键弹 TokenMenu popover（"加到包含 / 加到排除 / 复制值"），由
        // 父组件的 onTokenPick 实际执行 toggleTagInExpr。统一了 tag / pid /
        // level 三种字段的左键行为：都走 popover；pid / level 在 popover
        // 里仍是「只看此 / 排除此 / 复制值」。
        onClick={handle('tag', line.tag)}
        disabled={!onTokenClick}
        title={onTokenClick ? `按 tag ${line.tag} 操作` : undefined}
      >
        {line.tag}
      </button>
      <span className="text-ink-dim">: </span>
      <span className={lvCls}>{highlight(line.message, searchLower)}</span>
      {dupBadge}
    </>
  );
}

/**
 * Split `text` around (case-insensitive) substring `q` and wrap matches in a
 * yellow background span. Used by the in-list search bar to reveal hits.
 */
function highlight(text: string, q: string): React.ReactNode {
  if (!q || !text) return text;
  const lower = text.toLowerCase();
  let idx = lower.indexOf(q);
  if (idx < 0) return text;
  const out: React.ReactNode[] = [];
  let cursor = 0;
  let k = 0;
  while (idx >= 0) {
    if (idx > cursor) out.push(text.slice(cursor, idx));
    out.push(
      <mark key={k++} className="rounded bg-amber-400/40 px-0 text-ink">
        {text.slice(idx, idx + q.length)}
      </mark>
    );
    cursor = idx + q.length;
    idx = lower.indexOf(q, cursor);
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}
